/**
 * Upload Service
 *
 * File upload service for WiCloud — client for the /api/v1/uploads endpoints.
 * Handles payment proof uploads with the backend API.
 *
 * NOTE on the `api` wrapper (lib/api.ts): `api.post`/`api.get` already unwrap the
 * backend `StandardResponse<T>` envelope and return the inner `T` payload directly.
 * Do NOT read `.data.success` / `.data.data` on the result — that is the raw-axios
 * shape and will always be undefined here.
 */

import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errorMessages";

/**
 * Upload response structure (mirrors backend UploadFileResponse)
 */
interface UploadResponse {
  attachmentId: string;  // Guid from backend, serialized as string
  url: string;           // API-relative path to the auth-protected file
  fileName: string;
  size: number;
  contentType: string;
}

/**
 * Upload progress callback type
 */
type ProgressCallback = (percent: number) => void;

/**
 * Upload Service Methods
 *
 * Errors are normalized through `getErrorMessage`, which digs the specific rule
 * message out of the backend's FluentValidation 422 shape
 * (`error.details.errors.<Field>[0]`) instead of leaving the caller with the axios
 * string "Request failed with status code 422".
 */
export const uploadService = {
  /**
   * Upload a single file
   *
   * @param file - File to upload
   * @param onProgress - Optional progress callback (0-100)
   * @returns Upload result with attachmentId and URL
   */
  uploadFile: async (
    file: File,
    onProgress?: ProgressCallback,
  ): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    let result: UploadResponse;
    try {
      result = await api.post<UploadResponse>(
        "/uploads/file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(Math.min(percent, 100)); // Ensure max is 100
            }
          },
          timeout: 300000, // 5 minutes timeout for large files
          // The Upload UI renders per-file errors inline; suppress the generic global
          // toast so the user isn't shown two messages for the same failure.
          _suppressErrorToast: true,
        },
      );
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }

    // Fail loudly on a malformed envelope. Defaulting to empty strings would mark the
    // file "uploaded" in the UI while the attachment ID needed for submission is gone.
    if (!result?.attachmentId || !result?.url) {
      throw new Error("Upload succeeded but the server returned no attachment reference.");
    }

    return {
      attachmentId: String(result.attachmentId),
      url: result.url,
      fileName: result.fileName || file.name,
      size: result.size ?? file.size,
      contentType: result.contentType || file.type,
    };
  },

  /**
   * Fetch an auth-protected uploaded file and return an object URL suitable for
   * `<img src>` / `<a href>`.
   *
   * Files served by `/api/v1/uploads/files/*` are behind `[Authorize]`, so the `url`
   * returned by `uploadFile` CANNOT be used directly in an `<img src>` (the
   * browser sends no bearer token → 401) nor relied on to resolve to the API origin.
   * Fetch through this method instead — it goes through the authenticated `api` client.
   *
   * @param url - API-relative file path (the `url` from an upload result).
   * @returns Object URL. Caller MUST call `URL.revokeObjectURL()` when done.
   */
  downloadFile: async (url: string): Promise<string> => {
    // Upload responses contain the absolute API-relative path
    // (/api/v1/uploads/files/...). The api wrapper already prefixes requests
    // with /api/v1, so strip that prefix before requesting through the wrapper.
    const apiPath = url.replace(/^\/api\/v1(?=\/|$)/, "") || "/";
    const blob = await api.get<Blob>(apiPath, { responseType: "blob" });

    // Fail with something readable if the response was not binary. createObjectURL would throw a
    // bare TypeError, which callers tend to swallow into a silent "no image" state.
    if (!(blob instanceof Blob)) {
      throw new Error(
        `Expected a Blob from ${apiPath} but received ${typeof blob}. Check that responseType survived the request.`,
      );
    }

    return URL.createObjectURL(blob);
  },
};
